import {
  IntegrationProvider,
  IntegrationQuotaStatus,
  QuotaMeter,
  QuotaPeriod,
  type IntegrationQuotaRecord,
  type IntegrationQuotaWindowRecord
} from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiInfoBanner } from 'noddi-ui-web';
import { pluralize, pluralizeWithValue } from 'noddi-util';

const PROVIDER_LABEL: Record<IntegrationProvider, string> = {
  [IntegrationProvider.WHEEL_SIZE]: 'Wheel-Size'
};

/** Staff readout of metered upstream quotas. Hidden when the integration is not configured. */
export const IntegrationQuotaNotice = () => {
  const { data: quotaReport } = noddiAsync.useGet({
    type: URLKeys.getIntegrationQuotas,
    queryConfig: { staleTime: 60_000 }
  });

  const quotas = quotaReport?.quotas.filter((quota) => quota.status !== IntegrationQuotaStatus.NOT_CONFIGURED) ?? [];
  if (quotas.length === 0) {
    return null;
  }

  return (
    <div className='flex flex-col gap-4'>
      {quotas.map((quota) => (
        <IntegrationQuotaBanner key={quota.provider} quota={quota} />
      ))}
    </div>
  );
};

const IntegrationQuotaBanner = ({ quota }: { quota: IntegrationQuotaRecord }) => (
  <NoddiInfoBanner
    variant={quota.status === IntegrationQuotaStatus.OPEN ? 'info' : 'warning'}
    title={quotaTitle(quota)}
    texts={quotaTexts(quota)}
  />
);

function quotaTitle(quota: IntegrationQuotaRecord): string {
  const label = PROVIDER_LABEL[quota.provider];
  if (quota.status === IntegrationQuotaStatus.UNAVAILABLE) {
    return `${label} quota could not be read`;
  }
  if (quota.status !== IntegrationQuotaStatus.EXHAUSTED) {
    return `${label} quota`;
  }
  const callSpent = quota.windows.some(
    (usageWindow) => usageWindow.meter === QuotaMeter.CALL && usageWindow.remaining === 0
  );
  const searchSpent = quota.windows.some(
    (usageWindow) => usageWindow.meter === QuotaMeter.SEARCH && usageWindow.remaining === 0
  );
  if (!callSpent && searchSpent) {
    return `${label} searches are used up`;
  }
  return `${label} quota is used up`;
}

function quotaTexts(quota: IntegrationQuotaRecord): { value: string }[] | undefined {
  if (quota.status === IntegrationQuotaStatus.UNAVAILABLE) {
    return undefined;
  }
  const lines = quota.windows.map((usageWindow) => ({ value: windowLine(usageWindow) }));
  const providerPaused =
    quota.status === IntegrationQuotaStatus.EXHAUSTED &&
    quota.windows.length > 0 &&
    quota.windows.every((usageWindow) => usageWindow.remaining > 0);
  if (providerPaused) {
    lines.push({ value: 'The provider paused lookups.' });
  }
  if (quota.status === IntegrationQuotaStatus.EXHAUSTED && quota.retryAfterSeconds != null) {
    lines.push({ value: `Lookups resume in ${retryAfterLabel(quota.retryAfterSeconds)}.` });
  }
  return lines.length > 0 ? lines : undefined;
}

function windowLine(usageWindow: IntegrationQuotaWindowRecord): string {
  const amount = `${usageWindow.used} of ${usageWindow.limit}`;
  return `${amount} ${meterNoun(usageWindow.meter, usageWindow.limit)} ${periodWhen(usageWindow.period)}`;
}

function meterNoun(meter: QuotaMeter, count: number): string {
  switch (meter) {
    case QuotaMeter.CALL:
      return pluralize(count, 'request');
    case QuotaMeter.SEARCH:
      return pluralize(count, 'search', 'searches');
    default: {
      const unhandled: never = meter;
      return unhandled;
    }
  }
}

function periodWhen(period: QuotaPeriod): string {
  switch (period) {
    case QuotaPeriod.HOUR:
      return 'this hour';
    case QuotaPeriod.DAY:
      return 'today';
    default: {
      const unhandled: never = period;
      return unhandled;
    }
  }
}

function retryAfterLabel(seconds: number): string {
  if (seconds < 60) {
    return pluralizeWithValue({ value: seconds, word: 'second' });
  }
  if (seconds < 3600) {
    return pluralizeWithValue({ value: Math.ceil(seconds / 60), word: 'minute' });
  }
  return pluralizeWithValue({ value: Math.ceil(seconds / 3600), word: 'hour' });
}
